import { MonitorUp, Radio, Video } from 'lucide-react';
import { motion } from 'framer-motion';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { useArrivals, useShareStagePick } from './ShareStage';
import type { ParticipantTile, ScreenShareTile } from './roomState';
import { resolveShareStage } from './shareStage';
import { ScreenTile } from './ScreenTile';
import { VideoTile } from './VideoTile';

/**
 * The webinar scene (the mockup's stage). While a screen share is live the surface runs the
 * stage+rail pattern: the DOMINANT share fills the stage (object-contain, never cropped -
 * pick > newest share, the meeting grid's pin > share dominance mapped onto a broadcast
 * surface) and every OTHER live share renders as a real, full-size tile in the stage's side
 * rail, each with a pin button that moves it onto the stage - so two concurrent shares are
 * both visible at once, never a preview-only thumb. The publishers' camera line-up drops
 * into a fixed-height strip below. A share-less room keeps the camera grid stage; the
 * «Экран»/«Камера» row reads as the broadcast-source indicator (Экран lights up only while
 * a share is live - the stage and the strip carry both sources at once, so the tabs no
 * longer switch anything).
 */
export interface WebinarStageProps {
  readonly shares: readonly ScreenShareTile[];
  readonly stageTiles: readonly ParticipantTile[];
  /** Live presence roles, for the host badge on the stage tiles. */
  readonly roles: ReadonlyMap<string, 'admin' | 'moderator' | 'participant'>;
  /** Display names of the stage publishers for the presenter row (falls back to labels). */
  readonly mirrorOwnVideo: boolean;
}

export function WebinarStage(props: WebinarStageProps): JSX.Element {
  const { t } = useTranslation();
  const { picked, pickIdentity } = useShareStagePick(props.shares);
  // useArrivals already orders most-recent-first, exactly what resolveShareStage consumes.
  const model = resolveShareStage(props.shares, picked, useArrivals(props.shares));
  const share = model?.stage ?? null;
  const railShares = model?.rail ?? [];
  const presenter = props.stageTiles[0] ?? null;

  return (
    <div className="flex min-h-0 flex-1 flex-col p-2 sm:p-3">
      <div className="relative min-h-0 flex-1 overflow-hidden rounded-xl bg-zinc-900 ring-1 ring-white/[0.06]">
        {share !== null ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            data-testid="share-stage"
            aria-label={t(
              share.isLocal ? 'room.screen_aria_own' : 'room.screen_aria_other',
              share.isLocal ? undefined : { label: share.label },
            )}
            className="h-full w-full bg-zinc-950"
          >
            <div className={cn('flex h-full w-full gap-2', railShares.length > 0 && 'p-2')}>
              <div className="min-h-0 min-w-0 flex-1">
                <ScreenTile share={share} fill />
              </div>
              {railShares.length > 0 ? (
                // The side rail: every non-stage share as a real, full-size tile with a pin
                // button - concurrent shares stay visible at once, the pin switches the stage.
                <div
                  data-testid="share-rail"
                  aria-label={t('room.share_strip_aria')}
                  className="scroll-thin-dark flex h-full w-40 shrink-0 flex-col gap-2 overflow-y-auto sm:w-56"
                >
                  {railShares.map((railShare) => (
                    <ScreenTile
                      key={railShare.identity}
                      share={railShare}
                      onTogglePin={() => {
                        pickIdentity(railShare.identity);
                      }}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          </motion.div>
        ) : props.stageTiles.length > 0 ? (
          <div
            className={cn(
              'grid h-full w-full gap-2 p-2',
              props.stageTiles.length === 1
                ? 'grid-cols-1'
                : 'auto-rows-fr grid-cols-[repeat(auto-fit,minmax(min(100%,280px),1fr))]',
            )}
          >
            {props.stageTiles.map((tile) => (
              <div key={tile.identity} className="min-h-0 [&>div]:h-full [&>div]:aspect-auto">
                <VideoTile
                  tile={tile}
                  isAdmin={props.roles.get(tile.identity) === 'admin'}
                  mirror={props.mirrorOwnVideo && tile.isLocal}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-zinc-500">
            <Radio className="size-8 text-zinc-600" />
            <p className="text-[12.5px]">{t('webinar.stage_waiting')}</p>
          </div>
        )}
      </div>

      {share !== null && props.stageTiles.length > 0 ? (
        // The broadcast strip: the publishers' camera line-up at a stable size.
        <div
          data-testid="share-strip"
          aria-label={t('room.share_strip_aria')}
          className="scroll-thin-dark mt-2 flex h-24 shrink-0 items-center gap-2 overflow-x-auto overflow-y-hidden py-1 sm:h-28"
        >
          {props.stageTiles.map((tile) => (
            <div key={tile.identity} className="h-full w-36 shrink-0 sm:w-40">
              <VideoTile
                tile={tile}
                isAdmin={props.roles.get(tile.identity) === 'admin'}
                mirror={props.mirrorOwnVideo && tile.isLocal}
              />
            </div>
          ))}
        </div>
      ) : (
        <SourceRow hasShare={share !== null} presenter={presenter} />
      )}
    </div>
  );
}

/** The share-less bottom row: the broadcast-source indicator plus the presenter chip. */
function SourceRow({
  hasShare,
  presenter,
}: {
  readonly hasShare: boolean;
  readonly presenter: ParticipantTile | null;
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="mt-2.5 flex shrink-0 flex-wrap items-center gap-2">
      <div className="flex items-center gap-1 rounded-lg border border-white/[0.07] bg-white/[0.04] p-1">
        <span
          data-testid="stage-tab-screen"
          className={cn(
            'inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[12px] font-medium',
            hasShare ? 'bg-white text-zinc-950' : 'text-zinc-400 opacity-40',
          )}
        >
          <MonitorUp className="size-3.5" />
          {t('webinar.stage_screen')}
        </span>
        <span
          data-testid="stage-tab-camera"
          className={cn(
            'inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[12px] font-medium',
            !hasShare ? 'bg-white text-zinc-950' : 'text-zinc-400 opacity-40',
          )}
        >
          <Video className="size-3.5" />
          {t('webinar.stage_camera')}
        </span>
      </div>
      {presenter !== null ? (
        <div className="flex items-center gap-2.5 rounded-lg border border-white/[0.07] bg-white/[0.03] px-2.5 py-1.5">
          <span className="text-[12px] font-medium text-zinc-200">
            {presenter.isLocal ? t('webinar.presenter_you') : presenter.label}
          </span>
          <span className="hidden text-[11px] text-zinc-500 sm:inline">
            {t('webinar.presenter_hosting')}
          </span>
        </div>
      ) : null}
    </div>
  );
}
